/**
 * Sell Ready — podcast episode page enhancement.
 * Progressive enhancement over freeform article body content:
 * classifies Takeaways/Chapters/Resources/Quotes/Transcript blocks,
 * turns chapter timestamps into clickable seek buttons, collapses the
 * transcript, hardens the embedded YouTube iframe, and inserts a
 * contextual CTA. Every lookup guards for a missing element so an
 * episode missing one of these sections never throws. Each section
 * accepts a few heading aliases so past and future episodes can use
 * either phrasing without breaking (e.g. "Takeaways" or "Action Steps").
 */
(function () {
  'use strict';

  document.addEventListener('DOMContentLoaded', function () {
    var content = document.querySelector('[data-podcast-content]');
    if (!content) return;

    var ctaEnabled = content.getAttribute('data-cta-enabled') === 'true';
    var ctaLabel = content.getAttribute('data-cta-label') || '';
    var ctaLink = content.getAttribute('data-cta-link') || '#book';

    relocateBioIntro(content);

    var videoFrame = content.querySelector('iframe[src*="youtube.com"]');
    hardenVideoFrame(videoFrame);

    var takeawaysBlock = wrapBlock(
      findLabelElement(content, ['Takeaways', 'Action Steps']),
      'pe-block pe-block--takeaways'
    );
    var chaptersBlock = wrapBlock(
      findLabelElement(content, ['Chapters', "Here's a glimpse of what you'll learn", "What you'll learn"]),
      'pe-block pe-block--chapters'
    );
    wrapBlock(
      findLabelElement(content, ['Resources mentioned', 'Resources mentioned in this episode', 'Resources']),
      'pe-block pe-block--resources'
    );
    wrapBlock(
      findLabelElement(content, ['Quotable Moments', 'Quotes']),
      'pe-block pe-block--quotes'
    );
    var transcriptLabel = findLabelElement(content, 'Transcript');

    enhanceChapters(chaptersBlock, videoFrame);
    collapseTranscript(transcriptLabel);
    insertContextualCta(takeawaysBlock, ctaEnabled, ctaLabel, ctaLink);

    function hardenVideoFrame(frame) {
      if (!frame) return;
      frame.id = 'episode-player';
      if (!frame.hasAttribute('title')) {
        frame.setAttribute('title', 'Video: ' + document.title);
      }
      frame.setAttribute('loading', 'lazy');
      var src = frame.getAttribute('src') || '';
      if (src.indexOf('enablejsapi=1') === -1) {
        var sep = src.indexOf('?') === -1 ? '?' : '&';
        frame.setAttribute('src', src + sep + 'enablejsapi=1');
      }
    }

    function seekVideo(seconds) {
      if (!videoFrame || !videoFrame.contentWindow) return;
      videoFrame.contentWindow.postMessage(
        JSON.stringify({ event: 'command', func: 'seekTo', args: [seconds, true] }),
        '*'
      );
      videoFrame.contentWindow.postMessage(
        JSON.stringify({ event: 'command', func: 'playVideo', args: [] }),
        '*'
      );
      videoFrame.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    // Repositions the guest-bio intro (whatever paragraphs an author put
    // before the first heading in the article body) to sit directly above
    // the embedded video, wherever that video actually lives in the body —
    // matching the reference layout where the description leads the video
    // instead of trailing it. This is a same-container reorder, not a move
    // into the hero: the video stays exactly where it was authored.
    function relocateBioIntro(scope) {
      var nodes = collectLeadingNodes(scope);
      if (!nodes.length) return;

      var videoEl = scope.querySelector('iframe');
      if (!videoEl) return;
      var anchor = videoEl.closest('p') || videoEl;

      var bio = document.createElement('div');
      bio.className = 'pe-bio';
      nodes.forEach(function (node) {
        bio.appendChild(node);
      });
      anchor.parentNode.insertBefore(bio, anchor);
    }

    // Collects elements from the start of `scope` up to (not including) the
    // first heading-level element or <hr> — i.e. the unlabeled intro text.
    // Elements containing embedded media are skipped (left in place, not
    // moved) rather than treated as a boundary, so a video embed that
    // happens to lead the body doesn't block the real bio text after it
    // from being found.
    function collectLeadingNodes(scope) {
      var stopTags = /^(H[1-6]|HR)$/;
      var collected = [];
      var el = scope.firstElementChild;
      while (el && !stopTags.test(el.tagName)) {
        var next = el.nextElementSibling;
        if (el.tagName !== 'IFRAME' && !el.querySelector('iframe')) {
          collected.push(el);
        }
        el = next;
      }
      return collected;
    }

    // Finds the element (heading or bolded paragraph) whose text matches
    // any of `labels`, e.g. an <h2>Chapters</h2> or a <p><b>Transcript:</b></p>.
    // `labels` may be a single string or an array of accepted aliases.
    function findLabelElement(scope, labels) {
      var wantedList = (Array.isArray(labels) ? labels : [labels]).map(function (l) {
        return l.toLowerCase();
      });
      var candidates = scope.querySelectorAll('h2, h3, h4, p, strong, b');
      for (var i = 0; i < candidates.length; i++) {
        var text = candidates[i].textContent.trim().toLowerCase().replace(/:$/, '');
        if (wantedList.indexOf(text) !== -1) return candidates[i];
      }
      return null;
    }

    // Collects siblings following `startEl` up to (not including) the next
    // heading-level element or <hr>, without touching nesting elsewhere.
    function collectUntilNextBoundary(startEl) {
      var collected = [];
      var el = startEl.nextElementSibling;
      while (el && !/^H[1-6]$/.test(el.tagName) && el.tagName !== 'HR') {
        var next = el.nextElementSibling;
        collected.push(el);
        el = next;
      }
      return collected;
    }

    // Wraps `labelEl` and everything up to the next boundary in a new div
    // with `className`, in place, so it can be styled as a distinct block.
    function wrapBlock(labelEl, className) {
      if (!labelEl) return null;
      var nodes = collectUntilNextBoundary(labelEl);
      var wrapper = document.createElement('div');
      wrapper.className = className;
      labelEl.parentNode.insertBefore(wrapper, labelEl);
      wrapper.appendChild(labelEl);
      nodes.forEach(function (node) {
        wrapper.appendChild(node);
      });
      return wrapper;
    }

    // Replaces "00:00 Description" paragraphs/list items inside the
    // Chapters block with clickable seek buttons. Leaves anything that
    // doesn't match the timestamp pattern untouched.
    function enhanceChapters(block, frame) {
      if (!block) return;
      var timeRe = /^(\d{1,2}):(\d{2})\s+(.+)$/;
      var candidates = Array.prototype.slice.call(block.querySelectorAll('p, li'));

      candidates.forEach(function (el) {
        var match = el.textContent.trim().match(timeRe);
        if (!match) return;

        var totalSeconds = parseInt(match[1], 10) * 60 + parseInt(match[2], 10);

        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'pe-chapter';
        if (!frame) {
          btn.setAttribute('aria-disabled', 'true');
        }

        var timeSpan = document.createElement('span');
        timeSpan.className = 'pe-chapter-time';
        timeSpan.textContent = match[1] + ':' + match[2];

        var textSpan = document.createElement('span');
        textSpan.textContent = match[3];

        btn.appendChild(timeSpan);
        btn.appendChild(textSpan);
        btn.addEventListener('click', function () {
          seekVideo(totalSeconds);
        });

        el.replaceWith(btn);
      });
    }

    // Wraps the transcript body in a collapsed <details>/<summary> so the
    // full transcript isn't dumped into the page by default.
    function collapseTranscript(labelEl) {
      if (!labelEl) return;
      var nodes = collectUntilNextBoundary(labelEl);
      if (!nodes.length) return;

      var details = document.createElement('details');
      details.className = 'pe-transcript';

      var summary = document.createElement('summary');
      summary.textContent = 'Read full transcript';
      details.appendChild(summary);

      var body = document.createElement('div');
      nodes.forEach(function (node) {
        body.appendChild(node);
      });
      details.appendChild(body);

      labelEl.replaceWith(details);
    }

    // Inserts the theme-editor-configured CTA directly after the
    // Takeaways block, where reader intent is highest.
    function insertContextualCta(afterBlock, enabled, label, link) {
      if (!enabled || !label || !afterBlock) return;
      var cta = document.createElement('a');
      cta.className = 'pe-cta';
      cta.href = link;
      cta.textContent = label;
      afterBlock.parentNode.insertBefore(cta, afterBlock.nextSibling);
    }
  });
})();
